import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  ExternalLink,
  Search,
  ChevronDown,
  AlertTriangle,
  Calendar,
  Info,
  Navigation
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsulateDirectory = () => {
  const [countries, setCountries] = useState([]);
  const [consulates, setConsulates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedConsulate, setSelectedConsulate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [disclaimer, setDisclaimer] = useState("");

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchConsulatesByCountry(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API}/consulates/countries`);
      setCountries(res.data.countries || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsulatesByCountry = async (country) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/consulates/by-country/${encodeURIComponent(country)}`);
      setConsulates(res.data.consulates || []);
      setLastUpdated(res.data.last_updated || "");
      setDisclaimer(res.data.disclaimer || "");
      setSelectedConsulate(null);
    } catch (error) {
      console.error("Error fetching consulates:", error);
      setConsulates([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredConsulates = consulates.filter(c =>
    searchQuery === "" ||
    c.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeLabel = (type) => {
    return type === "embassy" ? "Embajada" : "Consulado";
  };

  const getTypeColor = (type) => {
    return type === "embassy" 
      ? "bg-amber-100 text-amber-700 border-amber-200" 
      : "bg-blue-100 text-blue-700 border-blue-200";
  };

  return (
    <div className="space-y-6" data-testid="consulate-directory">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-navy-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-amber-600" />
            Directorio de Consulados
          </h2>
          <p className="text-sm text-navy-600 mt-1">
            Información de embajadas y consulados de EE.UU. en Latinoamérica
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card-elevated p-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-navy-700 mb-2 block">
              Selecciona tu país
            </label>
            <Select 
              value={selectedCountry} 
              onValueChange={setSelectedCountry}
            >
              <SelectTrigger className="w-full" data-testid="country-select">
                <SelectValue placeholder="Seleccionar país..." />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.name}>
                    {country.name} ({country.consulate_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedCountry && consulates.length > 1 && (
            <div>
              <label className="text-sm font-medium text-navy-700 mb-2 block">
                Buscar ciudad
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                <Input
                  placeholder="Filtrar por ciudad..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="city-search"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent"></div>
        </div>
      ) : selectedCountry ? (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Consulates List */}
          <div className="lg:col-span-1 space-y-3">
            {filteredConsulates.length === 0 ? (
              <div className="card-elevated p-6 text-center">
                <Building2 className="w-10 h-10 text-navy-300 mx-auto mb-2" />
                <p className="text-navy-600">No se encontraron consulados</p>
              </div>
            ) : (
              filteredConsulates.map((consulate) => (
                <motion.div
                  key={consulate.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`card-elevated p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedConsulate?.id === consulate.id 
                      ? "ring-2 ring-amber-500 bg-amber-50/50" 
                      : ""
                  }`}
                  onClick={() => setSelectedConsulate(consulate)}
                  data-testid={`consulate-card-${consulate.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-navy-900 text-sm leading-tight">
                      {consulate.city}
                    </h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getTypeColor(consulate.type)}`}>
                      {getTypeLabel(consulate.type)}
                    </span>
                  </div>
                  <p className="text-xs text-navy-600 line-clamp-2">{consulate.name}</p>
                  {consulate.notes?.includes("IMPORTANTE") && (
                    <div className="flex items-center gap-1 mt-2 text-amber-600">
                      <AlertTriangle className="w-3 h-3" />
                      <span className="text-xs font-medium">Ver nota importante</span>
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>

          {/* Consulate Detail */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {selectedConsulate ? (
                <motion.div
                  key={selectedConsulate.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="card-elevated overflow-hidden"
                  data-testid="consulate-detail"
                >
                  {/* Header */}
                  <div className="bg-gradient-to-r from-navy-800 to-navy-900 p-6 text-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className={`text-xs px-2 py-1 rounded-full bg-white/20 mb-2 inline-block`}>
                          {getTypeLabel(selectedConsulate.type)}
                        </span>
                        <h3 className="text-xl font-bold">{selectedConsulate.name}</h3>
                        <p className="text-navy-200 mt-1 flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {selectedConsulate.city}, {selectedConsulate.country}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Alert for special cases */}
                  {selectedConsulate.notes?.includes("IMPORTANTE") && (
                    <div className="bg-amber-50 border-b border-amber-100 p-4 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-amber-800 text-sm">Nota Importante</p>
                        <p className="text-amber-700 text-sm mt-1">{selectedConsulate.notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-6 space-y-6">
                    {/* Address */}
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-navy-500" />
                        Dirección
                      </h4>
                      <p className="text-navy-700 text-sm">{selectedConsulate.address}</p>
                      {selectedConsulate.map_url && (
                        <a
                          href={selectedConsulate.map_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-2"
                        >
                          <Navigation className="w-4 h-4" />
                          Ver en Google Maps
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>

                    {/* Contact */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-navy-900 mb-2 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-navy-500" />
                          Teléfono
                        </h4>
                        <p className="text-navy-700 text-sm">{selectedConsulate.phone}</p>
                        {selectedConsulate.emergency_phone !== selectedConsulate.phone && (
                          <p className="text-navy-600 text-xs mt-1">
                            Emergencias: {selectedConsulate.emergency_phone}
                          </p>
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy-900 mb-2 flex items-center gap-2">
                          <Mail className="w-4 h-4 text-navy-500" />
                          Email
                        </h4>
                        <a
                          href={`mailto:${selectedConsulate.email}`}
                          className="text-blue-600 hover:text-blue-700 text-sm break-all"
                        >
                          {selectedConsulate.email}
                        </a>
                      </div>
                    </div>

                    {/* Hours */}
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-navy-500" />
                        Horarios
                      </h4>
                      <div className="bg-navy-50 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-navy-600">Servicios Consulares:</span>
                          <span className="text-navy-900 font-medium">{selectedConsulate.hours?.consular}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-navy-600">Atención Ciudadana:</span>
                          <span className="text-navy-900 font-medium">{selectedConsulate.hours?.citizen_services}</span>
                        </div>
                      </div>
                    </div>

                    {/* Links */}
                    <div className="flex flex-wrap gap-3 pt-2">
                      <a
                        href={selectedConsulate.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-navy-100 text-navy-700 rounded-lg hover:bg-navy-200 transition-colors text-sm font-medium"
                      >
                        <Globe className="w-4 h-4" />
                        Sitio Web Oficial
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      {selectedConsulate.appointment_url && selectedConsulate.appointment_url !== "N/A - Ver nota" && (
                        <a
                          href={selectedConsulate.appointment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors text-sm font-medium"
                        >
                          <Calendar className="w-4 h-4" />
                          Agendar Cita
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>

                    {/* Notes */}
                    {selectedConsulate.notes && !selectedConsulate.notes?.includes("IMPORTANTE") && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-blue-800 text-sm">{selectedConsulate.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="card-elevated p-12 text-center"
                >
                  <Building2 className="w-16 h-16 text-navy-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-navy-700 mb-2">
                    Selecciona un consulado
                  </h3>
                  <p className="text-navy-500 text-sm max-w-sm mx-auto">
                    Haz clic en cualquier consulado de la lista para ver su información detallada
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-elevated p-8 text-center"
        >
          <div className="max-w-md mx-auto">
            <Building2 className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-navy-900 mb-2">
              Encuentra tu Consulado
            </h3>
            <p className="text-navy-600 text-sm mb-6">
              Selecciona tu país para ver las embajadas y consulados de Estados Unidos disponibles 
              con información de contacto, horarios y enlaces para agendar citas.
            </p>
            <div className="bg-amber-50 rounded-lg p-4 text-left">
              <h4 className="font-semibold text-amber-800 text-sm mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Información incluida:
              </h4>
              <ul className="text-amber-700 text-sm space-y-1">
                <li>• Dirección y mapa de ubicación</li>
                <li>• Teléfonos de contacto y emergencias</li>
                <li>• Horarios de atención</li>
                <li>• Enlaces para agendar citas de visa</li>
                <li>• Notas importantes sobre servicios</li>
              </ul>
            </div>
          </div>
        </motion.div>
      )}

      {/* Disclaimer */}
      {disclaimer && selectedCountry && (
        <div className="text-center text-xs text-navy-500 py-2">
          <p>{disclaimer}</p>
          {lastUpdated && <p className="mt-1">Última actualización: {lastUpdated}</p>}
        </div>
      )}
    </div>
  );
};

export default ConsulateDirectory;
